import { useQuery } from '@apollo/client';
import { useMelwaysInfo } from '@seek/melways-react';
import { metrics } from '@seek/metrics-js';
import { useTranslations } from '@vocab/react';
import { Alert, Stack, Text } from 'braid-design-system';
import { useEffect } from 'react';

import { browserLogError } from 'src/modules/error-logger/browserLog';
import { isFeatureActive } from 'src/modules/features';
import { getCTPPreferenceQL } from 'src/modules/graphql/notifications/preference.graphql';
import { useTypedSelector } from 'src/reducers';
import type { GetCtpPreferenceQuery } from 'src/types/graphql';

import CategoryNew from '../category-new';
import { useUserProfile } from '../hooks/useUserProfile';
import { PageTitles } from '../pageTitle';

import translations from './.vocab';

const Preferences = () => {
  const { language } = useMelwaysInfo();
  const zone = useTypedSelector((state) => state.page.pageAnalytics.zone);
  const country = useTypedSelector(
    (state) => state.page.pageAnalytics.siteCountry,
  ).toUpperCase();
  const locale = `${language}-${country}`;
  const brand =
    PageTitles[useTypedSelector((state) => state.page.pageAnalytics.brand)] ??
    PageTitles.seek;

  const { authenticationStatus } = useUserProfile();
  const isAuthenticated = authenticationStatus === 'authenticated';

  const showHiddenPreferences = isFeatureActive('show-hidden-preferences');
  const showOnlyProductionReadyPreferences = !showHiddenPreferences;

  const { data, error } = useQuery<GetCtpPreferenceQuery>(getCTPPreferenceQL, {
    variables: {
      locale,
      zone,
      onlyProductionOption: showOnlyProductionReadyPreferences,
    },
  });

  const isQueryError = isAuthenticated && error;

  const { t } = useTranslations(translations);

  useEffect(() => {
    if (isQueryError) {
      metrics.count('preferences-error-page-rendered');
    } else {
      metrics.count('preferences-page-rendered');
    }
  }, [error, isAuthenticated, isQueryError]);

  if (isQueryError) {
    browserLogError(error);
    return (
      <Alert tone="critical">
        <Text>{t('preferences-alert-message')}</Text>
      </Alert>
    );
  }

  return (
    <Stack space="xlarge">
      <Alert tone="info">
        <Text>
          {t('preferences-info-alert-message', {
            brand,
          })}
        </Text>
      </Alert>
      <Stack space="gutter">
        {data?.viewer?.notificationCTPPreferenceGroups?.map((x, i) => (
          <CategoryNew
            key={i}
            category={x.category}
            preferences={x.preferences}
          />
        ))}
      </Stack>
    </Stack>
  );
};

export default Preferences;
